import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiEye, FiEyeOff, FiMapPin, FiTruck } from 'react-icons/fi';
import { getServices, createService, updateService, deleteService } from '../services/services';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import Pagination from '../components/Common/Pagination';
import ServiceForm from '../components/Services/ServiceForm';
import TransferForm from '../components/Services/TransferForm';
import toast from 'react-hot-toast';

const categoryColors = {
  adventure_desert: 'bg-orange-100 text-orange-800',
  boat_cruise: 'bg-blue-100 text-blue-800',
  city_breaks: 'bg-green-100 text-green-800',
  cultural_historical: 'bg-purple-100 text-purple-800',
  luxury_premium: 'bg-yellow-100 text-yellow-800',
};

const categoryLabels = {
  adventure_desert: 'Adventure & Desert',
  boat_cruise: 'Boat & Cruise',
  city_breaks: 'City Breaks',
  cultural_historical: 'Cultural & Historical',
  luxury_premium: 'Luxury & Premium',
};

const serviceTypeLabels = {
  tour: 'Tour',
  transfer: 'Transfer',
};

export default function Services() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showTransferForm, setShowTransferForm] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [filters, setFilters] = useState({ 
    service_type: '', 
    category: '', 
    location: '', 
    search: '', 
    page: 1, 
    limit: 20 
  });
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchServices();
  }, [filters]);

  const fetchServices = async () => {
    setLoading(true);
    try {
      const params = { ...filters };
      if (filters.service_type) params.service_type = filters.service_type;
      const data = await getServices(params);
      setServices(data.data || []);
      setTotalPages(data.pagination?.pages || 1);
    } catch (error) {
      toast.error('Failed to fetch services');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (serviceData) => {
    try {
      await createService(serviceData);
      toast.success('Service created successfully');
      setShowForm(false);
      fetchServices();
    } catch (error) {
      toast.error('Failed to create service');
    }
  };

  const handleCreateTransfer = async (transferData) => {
    try {
      await createService(transferData);
      toast.success('Transfer route created successfully');
      setShowTransferForm(false);
      fetchServices();
    } catch (error) {
      toast.error('Failed to create transfer route');
    }
  };

  const handleUpdate = async (id, serviceData) => {
    try {
      await updateService(id, serviceData);
      toast.success('Service updated successfully');
      setEditingService(null);
      fetchServices();
    } catch (error) {
      toast.error('Failed to update service');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    try {
      await deleteService(id);
      toast.success('Service deleted successfully');
      fetchServices();
    } catch (error) {
      toast.error('Failed to delete service');
    }
  };

  const getMinPrice = (service) => {
    if (service.service_type === 'transfer') {
      if (service.transfer_routes?.length > 0) {
        const minPrice = Math.min(...service.transfer_routes.flatMap(r => r.prices.map(p => p.price)));
      return minPrice ? `$${minPrice}` : '-';
      }
      return '-';
    } else {
      if (service.prices?.length > 0) {
      const minPrice = Math.min(...service.prices.map(p => p.adult_price || 0));
      return minPrice > 0 ? `$${minPrice}` : '-';
      }
      return '-';
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Services</h1>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowTransferForm(true)}
            className="flex items-center px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition"
          >
            <FiTruck className="mr-2" /> Add Transfer Route
          </button>
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition"
          >
            <FiPlus className="mr-2" /> Add Tour
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm text-gray-600 mb-1">Service Type</label>
            <select
              value={filters.service_type}
              onChange={(e) => setFilters({ ...filters, service_type: e.target.value, page: 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">All</option>
              <option value="tour">Tours</option>
              <option value="transfer">Transfers</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm text-gray-600 mb-1">Category (Tours)</label>
            <select
              value={filters.category}
              onChange={(e) => setFilters({ ...filters, category: e.target.value, page: 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              disabled={filters.service_type === 'transfer'}
            >
              <option value="">All Categories</option>
              {Object.entries(categoryLabels).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm text-gray-600 mb-1">Location</label>
            <select
              value={filters.location}
              onChange={(e) => setFilters({ ...filters, location: e.target.value, page: 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">All</option>
              <option value="hurghada">Hurghada</option>
              <option value="marsa_alam">Marsa Alam</option>
              <option value="luxor">Luxor</option>
              <option value="aswan">Aswan</option>
              <option value="cairo">Cairo</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm text-gray-600 mb-1">Search</label>
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Name"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Services Table */}
      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category/Location</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price From</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {services.map((service) => (
                  <tr key={service._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        service.service_type === 'transfer' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {serviceTypeLabels[service.service_type]}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-800">{service.name}</p>
                        <p className="text-xs text-gray-500 truncate max-w-xs">{service.short_description}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {service.service_type === 'transfer' ? (
                        <div className="flex flex-wrap gap-1">
                          <FiMapPin className="inline text-gray-400 mr-1" />
                          <span className="text-sm">
                            {service.transfer_routes?.length || 0} routes
                          </span>
                        </div>
                      ) : (
                        <span className={`px-2 py-1 text-xs rounded-full ${categoryColors[service.category]}`}>
                          {categoryLabels[service.category]}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 font-medium">{getMinPrice(service)}</td>
                    <td className="px-6 py-4">
                      {service.status === 'active' ? (
                        <span className="flex items-center text-green-600 text-sm">
                          <FiEye className="mr-1" /> Active
                        </span>
                      ) : (
                        <span className="flex items-center text-gray-500 text-sm">
                          <FiEyeOff className="mr-1" /> Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => setEditingService(service)}
                        className="text-blue-500 hover:text-blue-700 mr-3"
                      >
                        <FiEdit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(service._id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                     </td>
                   </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {services.length === 0 && (
            <div className="text-center py-12 text-gray-500">No services found</div>
          )}
          
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t">
              <Pagination
                currentPage={filters.page}
                totalPages={totalPages}
                onPageChange={(page) => setFilters({ ...filters, page })}
              />
            </div>
          )}
        </div>
      )}

      {/* Tour Form Modal */}
      <ServiceForm
        isOpen={showForm || (!!editingService && editingService.service_type === 'tour')}
        onClose={() => {
          setShowForm(false);
          setEditingService(null);
        }}
        onSubmit={editingService ? (data) => handleUpdate(editingService._id, data) : handleCreate}
        initialData={editingService?.service_type === 'tour' ? editingService : null}
      />

      {/* Transfer Form Modal */}
      <TransferForm
        isOpen={showTransferForm || (!!editingService && editingService.service_type === 'transfer')}
        onClose={() => {
          setShowTransferForm(false);
          setEditingService(null);
        }}
        onSubmit={editingService ? (data) => handleUpdate(editingService._id, data) : handleCreateTransfer}
        initialData={editingService?.service_type === 'transfer' ? editingService : null}
      />
    </div>
  );
}
